import * as React from 'react';
import { connect } from 'react-redux';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CardMedia,
    CircularProgress,
    FormControlLabel,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { getHomeBanner, updateHomeBanner } from '../../../actions/misc';
import { Banner } from '../../../api/misc.types';
import { ReduxState } from '../../../reducers';

// Dimensions/constants to avoid magic numbers
const CARD_MAX_WIDTH = 800;
const PREVIEW_HEIGHT = 280;
const BANNER_MAX_WIDTH = 500;
const BORDER_RADIUS = 20;

interface StateProps {
    loadingBanner: boolean;
    homeBanner: Banner;
}

interface DispatchProps {
    getHomeBanner: () => void;
    updateHomeBanner: (banner: Banner) => void;
}

type Props = StateProps & DispatchProps;

interface State {
    draft: Banner;
}

class HomeBanner extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            draft: { ...props.homeBanner },
        };
    }

    componentDidMount(): void {
        this.props.getHomeBanner();
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        // Keep local draft in sync when store updates (e.g., after load/save)
        if (prevProps.homeBanner !== this.props.homeBanner) {
            // Replace entire draft to reflect latest server state
            this.setState({ draft: { ...this.props.homeBanner } });
        }
    }

    // Handlers (avoid creating inline functions in render)
    private handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.updateDraft({ title: e.target.value });
    };

    private handleImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.updateDraft({ imageUrl: e.target.value });
    };

    private handleLinkUrlChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.updateDraft({ linkUrl: e.target.value });
    };

    private handleEnabledChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.updateDraft({ enabled: e.target.checked });
    };

    private onSave = (): void => {
        const { draft } = this.state;
        this.props.updateHomeBanner(draft);
    };

    private onReset = (): void => {
        this.setState({ draft: { ...this.props.homeBanner } });
    };

    private onOpenPreviewLink = (): void => {
        const { linkUrl } = this.state.draft;
        if (linkUrl) {
            window.open(linkUrl, '_blank', 'noopener,noreferrer');
        }
    };

    // Helpers
    private updateDraft(patch: Partial<Banner>): void {
        this.setState((prev) => ({ draft: { ...prev.draft, ...patch } }));
    }

    private isDirty(): boolean {
        const { draft } = this.state;
        const { homeBanner } = this.props;
        return (
            draft.title !== homeBanner.title ||
            draft.imageUrl !== homeBanner.imageUrl ||
            draft.linkUrl !== homeBanner.linkUrl ||
            draft.enabled !== homeBanner.enabled
        );
    }

    private validationError(): string | null {
        const { draft } = this.state;
        // If enabled, require an imageUrl. Link is optional but recommended.
        if (draft.enabled && !draft.imageUrl) {
            return 'Image URL is required when the banner is enabled.';
        }
        return null;
    }

    render(): React.ReactNode {
        const { loadingBanner } = this.props;
        const { draft } = this.state;

        const error = this.validationError();
        const canSave = this.isDirty() && !error && !loadingBanner;

        const shouldDisplayBanner = draft.enabled && Boolean(draft.imageUrl);

        return (
            <Card sx={{ maxWidth: CARD_MAX_WIDTH, mt: 5 }}>
                <CardHeader title="Home Banner" subheader="Preview and edit banner settings" />
                <CardContent>
                    {/* Preview (mirrors mobile layout semantics) */}
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box
                            sx={{
                                maxWidth: BANNER_MAX_WIDTH,
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    width: '92%',
                                    height: PREVIEW_HEIGHT,
                                    borderRadius: `${BORDER_RADIUS}px`,
                                    overflow: 'hidden',
                                    boxShadow: (theme) => theme.shadows[5],
                                    bgcolor: '#bdbdbd',
                                    position: 'relative',
                                    cursor: draft.linkUrl ? 'pointer' : 'default',
                                }}
                                onClick={this.onOpenPreviewLink}
                            >
                                {shouldDisplayBanner ? (
                                    <CardMedia
                                        component="img"
                                        image={draft.imageUrl}
                                        alt={draft.title || 'Home banner'}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        <Typography variant="body2">
                                            {draft.enabled
                                                ? 'Provide an Image URL to display the banner'
                                                : 'Banner disabled'}
                                        </Typography>
                                    </Box>
                                )}
                                {draft.title && (
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 'bold',
                                            pl: 3,
                                            mt: 1,
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            color: 'white',
                                        }}
                                    >
                                        {draft.title}
                                    </Typography>
                                )}
                                {loadingBanner && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'rgba(255,255,255,0.5)',
                                        }}
                                    >
                                        <CircularProgress size={32} />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Editor */}
                    <Box sx={{ mt: 3 }}>
                        <Stack spacing={2}>
                            <FormControlLabel
                                control={<Switch color="primary" checked={draft.enabled} onChange={this.handleEnabledChange} />}
                                label="Enabled"
                            />
                            <TextField
                                label="Title"
                                value={draft.title}
                                fullWidth
                                onChange={this.handleTitleChange}
                            />
                            <TextField
                                label="Image URL"
                                value={draft.imageUrl}
                                fullWidth
                                onChange={this.handleImageUrlChange}
                                error={Boolean(error) && draft.enabled}
                                helperText={draft.enabled ? error : ''}
                            />
                            <TextField
                                label="Link URL (optional)"
                                value={draft.linkUrl}
                                fullWidth
                                onChange={this.handleLinkUrlChange}
                            />
                        </Stack>
                    </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between' }}>
                    <Box>
                        <Button onClick={this.onReset} disabled={!this.isDirty() || loadingBanner} color="inherit">
                            Reset
                        </Button>
                    </Box>
                    <Box>
                        <Button onClick={this.onOpenPreviewLink} disabled={!this.state.draft.linkUrl} sx={{ mr: 1 }}>
                            Open link
                        </Button>
                        <Button variant="contained" onClick={this.onSave} disabled={!canSave}>
                            {loadingBanner ? 'Saving…' : 'Save'}
                        </Button>
                    </Box>
                </CardActions>
            </Card>
        );
    }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
    loadingBanner: Boolean(state.misc.loadingBanner),
    homeBanner: state.misc.homeBanner || { imageUrl: '', linkUrl: '', title: '', enabled: false },
});

export default connect<StateProps, DispatchProps, unknown, ReduxState>(mapStateToProps, {
    getHomeBanner,
    updateHomeBanner,
})(HomeBanner);
